/* eslint-disable no-plusplus */
/**
 * Wraps an async function to limit its concurrency to 1 and ensure that it
 * processes invocations in order.
 *
 * If one of the queued promises rejects, `onError` is called. This is to
 * prevent unhandled promise rejections.
 *
 * This function also has a property `queueLength` that can be used to check how
 * many invocations are currently waiting to be processed.
 */
export const asSequentialAsync = (fn, onError = console.error) => {
  /**
   * A queue of promises that are waiting to be processed.
   *
   * NOTE: this isn't an array of promises, it's a "chain" of promises (i.e.
   * each promise is chained to the previous one).
   */
  let queue = Promise.resolve();

  // We return a new function that wraps the original function
  const wrapped = (...args) => {
    // We increment the queue length when a new invocation is queued
    wrapped.queueLength++;

    return new Promise((resolve) => {
      // We chain the new invocation to the end of the queue
      queue = queue.then(() =>
        fn(...args)
          .then(resolve)
          .catch((err) => {
            onError(err);
            resolve();
          })
          .finally(() => {
            // Once the invocation completes, we decrement the queue length
            wrapped.queueLength--;
          }),
      );
    });
  };

  // This tracks how many invocations have been queued but not run
  wrapped.queueLength = 0;

  return wrapped;
};
